import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { AppDispatch, RootState } from '../../../shared/store'
import {
  setOnlineStatus,
  processSync,
  getQueueStats,
  clearFailedItems,
  retryFailedItem,
  discardFailedItem,
} from '../../../shared/store/slices/syncSlice'

/**
 * Custom hook for sync status
 * - Monitors online/offline status
 * - Automatically syncs when connection restored
 * - Provides sync actions and state
 */
export const useSyncStatus = () => {
  const dispatch = useDispatch<AppDispatch>()
  const sync = useSelector((state: RootState) => state.sync)

  useEffect(() => {
    // Update online status
    const handleOnline = () => {
      dispatch(setOnlineStatus(true))
      // Automatically process sync queue when coming online
      void dispatch(processSync())
    }

    const handleOffline = () => {
      dispatch(setOnlineStatus(false))
    }

    // Set initial status
    dispatch(setOnlineStatus(navigator.onLine))

    // Listen for online/offline events
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Get initial queue stats
    void dispatch(getQueueStats())

    // Cleanup listeners
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [dispatch])

  return {
    // State
    isOnline: sync.isOnline,
    isSyncing: sync.isSyncing,
    lastSyncTime: sync.lastSyncTime,
    pendingCount: sync.pendingCount,
    failedCount: sync.failedCount,
    failedItems: sync.failedItems,
    error: sync.error,

    // Actions
    sync: () => dispatch(processSync()),
    refreshStats: () => dispatch(getQueueStats()),
    clearFailed: () => dispatch(clearFailedItems()),
    retryItem: (id: string) => dispatch(retryFailedItem(id)),
    discardItem: (id: string) => dispatch(discardFailedItem(id)),

    // Computed values
    hasPendingItems: sync.pendingCount > 0,
    hasFailedItems: sync.failedCount > 0,
  }
}
